import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Clock, ArrowRight, ChevronLeft, IndianRupee, Award, Zap, Shield } from "lucide-react";
import PageSEO from "@/components/PageSEO";
import { blogPosts } from "@/data/blogPosts";

const HERO_ICONS = { IndianRupee, Award, Zap, Shield };

const F = {
  950: "#2d2219", 900: "#3a2e28", 800: "#504136", 700: "#5e4e41",
  500: "#689689", 400: "#83E8BA", 300: "#8ab0a8",
  200: "#B2E6D4", 100: "#c8ede4", 50: "#edf8f4",
  e500: "#A49E8D", e300: "#c0b9ac", e100: "#f0ede8",
  m600: "#2d4f4b", m500: "#3d6b65", m400: "#4a7068",
  c100: "#f5f2ec", c200: "#e8e3d8", c50: "#faf9f6",
};

const CATEGORY_COLORS = {
  "CAPEX & Finance":       { bg: "#edf8f4", text: "#2d4f4b", border: "#B2E6D4" },
  "Subsidies & Finance":   { bg: "#f0ede8", text: "#504136", border: "#c0b9ac" },
  "Equipment & Vendors":   { bg: "#edf8f4", text: "#2d4f4b", border: "#B2E6D4" },
  "Regulations & Compliance": { bg: "#f0ede8", text: "#504136", border: "#c0b9ac" },
};

function CategoryBadge({ category }) {
  const col = CATEGORY_COLORS[category] || { bg: F[50], text: F.m600, border: F[200] };
  return (
    <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: col.bg, color: col.text, border: `1px solid ${col.border}` }}>
      {category}
    </span>
  );
}

export default function BlogList() {
  const navigate = useNavigate();
  const [featured, ...rest] = blogPosts;

  return (
    <div className="min-h-screen" style={{ backgroundColor: F.c100 }}>
      <PageSEO
        title="EV Hub Studio Blog — EV Charging Station Guides for India"
        description="In-depth guides on EV charging station costs, FAME III subsidies, charger vendors, and compliance for India. Plan smarter with EV Hub Studio."
        path="/blog"
      />

      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur" style={{ backgroundColor: `${F[800]}f0`, borderBottom: `1px solid ${F[700]}` }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate("/")} className="flex items-center gap-2.5 transition-opacity hover:opacity-80">
              <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: F[500] }}>
                <Leaf className="w-4 h-4" style={{ color: F.c50 }} />
              </div>
              <span className="font-black text-sm" style={{ fontFamily: "Chivo, sans-serif", color: F.c50 }}>EV Hub Studio</span>
            </button>
            <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm transition-colors" style={{ color: F[200] }}
              onMouseEnter={e => e.currentTarget.style.color = F.c50}
              onMouseLeave={e => e.currentTarget.style.color = F[200]}>
              <ChevronLeft className="w-4 h-4" /> Home
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-6xl">

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-12 text-center">
          <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: F.m400 }}>EV Hub Studio</p>
          <h1 className="text-3xl sm:text-4xl font-black mb-4" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
            EV Charging Insights for India
          </h1>
          <p className="text-base max-w-2xl mx-auto" style={{ color: F.m600 }}>
            Practical guides on costs, subsidies, equipment, and compliance — for developers planning EV charging stations in India.
          </p>
        </motion.div>

        {/* Featured post */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-10 rounded-2xl overflow-hidden cursor-pointer group"
          style={{ border: `1px solid ${F[700]}` }}
          onClick={() => navigate(`/blog/${featured.slug}`)}>
          {/* Featured hero image */}
          {featured.hero?.image && (
            <div className="relative overflow-hidden" style={{ height: 200 }}>
              <img src={featured.hero.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 50%)" }} />
              <div className="absolute bottom-4 left-6 flex flex-wrap gap-2">
                {featured.hero.chips.slice(0, 3).map((chip) => (
                  <span key={chip} className="text-xs px-2.5 py-1 rounded-full backdrop-blur-sm"
                    style={{ background: "rgba(0,0,0,0.45)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)" }}>{chip}</span>
                ))}
              </div>
              <div className="absolute top-4 right-5 px-3 py-2 rounded-xl backdrop-blur-sm text-right"
                style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.15)" }}>
                <p className="text-xl font-black" style={{ color: F[400], fontFamily: "Chivo, sans-serif" }}>{featured.hero.stat}</p>
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>{featured.hero.statLabel}</p>
              </div>
            </div>
          )}
          <div className="p-8 sm:p-10" style={{ background: F[900] }}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: F[400] }}>Featured</span>
              <span style={{ color: F[700] }}>·</span>
              <CategoryBadge category={featured.category} />
            </div>
            <h2 className="text-xl sm:text-2xl font-black mb-3 group-hover:underline" style={{ fontFamily: "Chivo, sans-serif", color: F.c50 }}>
              {featured.title}
            </h2>
            <p className="text-sm mb-5 leading-relaxed" style={{ color: F[300] }}>{featured.description}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-xs" style={{ color: F[300] }}>
                <span>{featured.date}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{featured.readTime}</span>
              </div>
              <span className="flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all" style={{ color: F[400] }}>
                Read article <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </motion.div>

        {/* Article grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((post, i) => {
            const Icon = post.hero ? (HERO_ICONS[post.hero.icon] || Zap) : Zap;
            const [from, to] = post.hero ? post.hero.gradient : [F[900], F[950]];
            return (
            <motion.article
              key={post.slug}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
              className="rounded-xl overflow-hidden cursor-pointer group flex flex-col"
              style={{ background: F.c50, border: `1px solid ${F.c200}` }}
              onClick={() => navigate(`/blog/${post.slug}`)}>
              {/* Thumbnail */}
              <div className="relative overflow-hidden" style={{ height: 140 }}>
                {post.hero?.image ? (
                  <>
                    <img src={post.hero.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 60%)" }} />
                    <div className="absolute bottom-2 right-2 px-2 py-1 rounded-lg"
                      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
                      <p className="text-xs font-black" style={{ color: F[400], fontFamily: "Chivo, sans-serif" }}>{post.hero.stat}</p>
                    </div>
                  </>
                ) : post.hero?.images ? (
                  <div className="grid grid-cols-4 h-full" style={{ background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}>
                    {post.hero.images.map((img) => (
                      <div key={img.src} className="flex items-center justify-center p-2" style={{ background: "rgba(0,0,0,0.3)", borderRight: "1px solid rgba(255,255,255,0.05)" }}>
                        <img src={img.src} alt={img.label} className="max-h-full max-w-full object-contain" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-between h-full px-5" style={{ background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}>
                    <Icon className="w-10 h-10 opacity-20" style={{ color: F[400] }} />
                    <div className="text-right">
                      <p className="text-xl font-black" style={{ color: F[400], fontFamily: "Chivo, sans-serif" }}>{post.hero?.stat}</p>
                      <p className="text-xs" style={{ color: F[300] }}>{post.hero?.statLabel}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="mb-3">
                  <CategoryBadge category={post.category} />
                </div>
                <h2 className="text-base font-black mb-2 leading-snug group-hover:underline flex-1" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
                  {post.title}
                </h2>
                <p className="text-xs leading-relaxed mb-4" style={{ color: F.m600 }}>{post.description}</p>
                <div className="flex items-center justify-between mt-auto pt-4" style={{ borderTop: `1px solid ${F.c200}` }}>
                  <div className="flex items-center gap-3 text-xs" style={{ color: F.m400 }}>
                    <span>{post.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ color: F[500] }} />
                </div>
              </div>
            </motion.article>
            );
          })}
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8" style={{ background: F[950], borderTop: `1px solid ${F[800]}` }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm" style={{ color: F.m400 }}>© {new Date().getFullYear()} Stuthi Energy Private Limited</p>
          <div className="flex items-center gap-4 text-sm" style={{ color: F.m400 }}>
            <a href="/privacy" style={{ color: F.m400 }} onMouseEnter={e => e.currentTarget.style.color = F[300]} onMouseLeave={e => e.currentTarget.style.color = F.m400}>Privacy</a>
            <a href="/terms" style={{ color: F.m400 }} onMouseEnter={e => e.currentTarget.style.color = F[300]} onMouseLeave={e => e.currentTarget.style.color = F.m400}>Terms</a>
            <a href="/contact" style={{ color: F.m400 }} onMouseEnter={e => e.currentTarget.style.color = F[300]} onMouseLeave={e => e.currentTarget.style.color = F.m400}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
